import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Paperclip, X, Ban, ArrowRight, Download } from "lucide-react";
import api from "@/lib/api";
import { formatINR } from "@/utils/formatINR";

const STATUS_ORDER = ["received", "costing", "quote_sent", "won"];
const STATUS_LABEL = {
  received: "Received", costing: "Costing", quote_sent: "Quote Sent",
  won: "Won", lost: "Lost", dropped: "Dropped",
};

function Cell({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm mt-1">{value ?? "—"}</div>
    </div>
  );
}

export default function RfqDetail() {
  const { rfqId } = useParams();
  const navigate = useNavigate();
  const [rfq, setRfq] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    const r = await api.get(`/rfqs/${rfqId}`);
    setRfq(r.data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rfqId]);

  async function setStatus(action) {
    if (!window.confirm(`Mark this RFQ as ${action.replace("mark-", "")}?`)) return;
    setBusy(true);
    try { await api.post(`/rfqs/${rfqId}/${action}`); await load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }

  async function convert() {
    if (!window.confirm("Convert this RFQ to a Costing? This creates an NPD (1 part) or Project (2+ parts) and is one-way.")) return;
    setBusy(true);
    try {
      const r = await api.post(`/rfqs/${rfqId}/convert`);
      if (r.data.linked_project_id) navigate(`/projects/${r.data.linked_project_id}`);
      else if (r.data.linked_npd_id) navigate(`/npd/${r.data.linked_npd_id}`);
    } catch (e) { alert(e.response?.data?.detail || e.message); setBusy(false); }
  }

  if (!rfq) return <div className="p-6 text-slate-400">Loading…</div>;
  const closed = ["won", "lost", "dropped"].includes(rfq.status);
  const wonIdx = STATUS_ORDER.indexOf(rfq.status);

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="rfq-detail-page">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-xs font-mono text-slate-500">{rfq.rfq_number}</div>
          <h1 className="text-xl font-semibold mt-1">{rfq.customer_name} — {rfq.model_name || "(no model)"}</h1>
        </div>
        <div className="flex gap-2">
          {!closed && !rfq.linked_npd_id && !rfq.linked_project_id && (
            <button onClick={convert} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50" data-testid="rfq-convert-btn">
              <ArrowRight size={14} /> Convert to Costing
            </button>
          )}
          {!closed && (
            <>
              <button onClick={() => setStatus("mark-lost")} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded-md hover:bg-red-50">
                <X size={14} /> Mark Lost
              </button>
              <button onClick={() => setStatus("mark-dropped")} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50">
                <Ban size={14} /> Mark Dropped
              </button>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 mb-4 text-xs flex-wrap">
        {STATUS_ORDER.map((s, i) => {
          const done = i < wonIdx;
          const curr = i === wonIdx;
          const cls = done ? "bg-emerald-100 text-emerald-800" : curr ? "bg-amber-100 text-amber-800 border border-amber-400" : "bg-slate-100 text-slate-400";
          return (
            <div key={s} className="flex items-center gap-1">
              <span className={`px-2.5 py-1 rounded-full ${cls}`}>{STATUS_LABEL[s]}</span>
              {i < STATUS_ORDER.length - 1 && <span className="text-slate-300">›</span>}
            </div>
          );
        })}
        <span className="text-slate-300 mx-1">·</span>
        <span className={`px-2.5 py-1 rounded-full ${rfq.status === "lost" ? "bg-red-100 text-red-800" : "bg-slate-100 text-slate-400"}`}>Lost</span>
        <span className="text-slate-300">›</span>
        <span className={`px-2.5 py-1 rounded-full ${rfq.status === "dropped" ? "bg-stone-300 text-stone-800" : "bg-slate-100 text-slate-400"}`}>Dropped</span>
      </div>
      {(rfq.linked_npd_id || rfq.linked_project_id) && (
        <div className="bg-sky-50 border border-sky-200 rounded-md p-3 mb-3 flex items-center gap-2 text-sm text-sky-800">
          <ArrowRight size={16} />
          <span>Converted on {rfq.converted_at?.slice(0, 10)} —</span>
          {rfq.linked_project_id ? <Link to={`/projects/${rfq.linked_project_id}`} className="font-medium hover:underline">Open Project →</Link>
                                  : <Link to={`/npd/${rfq.linked_npd_id}`} className="font-medium hover:underline">Open NPD →</Link>}
        </div>
      )}
      {rfq.mail_attachment_original_filename && (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 mb-3 flex items-center gap-2 text-sm">
          <Paperclip size={16} className="text-slate-600" />
          <span className="flex-1"><span className="font-medium">Mail Attachment</span> · {rfq.mail_attachment_original_filename} ({Math.round((rfq.mail_attachment_size_bytes || 0) / 1024)} KB)</span>
          <a href={`${api.defaults.baseURL}/rfqs/${rfqId}/attachment`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-700 text-xs"><Download size={12} /> Download</a>
        </div>
      )}
      <div className="grid grid-cols-3 gap-4 bg-white border border-slate-200 rounded-lg p-4 mb-4">
        <Cell label="Customer" value={rfq.customer_name} />
        <Cell label="Received" value={rfq.received_date} />
        <Cell label="Owner" value={rfq.owner_name || rfq.assigned_to} />
        <Cell label="Parts" value={(rfq.parts || []).length} />
        <Cell label="Business value (live)" value={formatINR(rfq.business_value_live || rfq.manual_business_value || 0)} />
        <Cell label="Plant" value={rfq.unit_code ? `${rfq.unit_code}${rfq.unit_name ? ' — ' + rfq.unit_name : ''}` : rfq.unit_id} />
      </div>
      {(rfq.linked_npd_id || rfq.linked_project_id) && (
        <p className="text-xs text-slate-500 mb-4">Manual estimate at intake: {formatINR(rfq.manual_business_value || 0)} · replaced by computed total from the linked costing.</p>
      )}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-4">
        <div className="px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200">Parts</div>
        <div className="overflow-x-auto">
        <table className="w-full min-w-[600px] text-xs">
          <thead className="text-slate-500">
            <tr><th className="text-left px-4 py-2 font-medium">Part name</th><th className="text-left px-4 py-2 font-medium">Part #</th><th className="text-left px-4 py-2 font-medium">Monthly qty</th><th className="text-left px-4 py-2 font-medium">Target ₹/pc</th><th className="text-left px-4 py-2 font-medium">Drawing</th></tr>
          </thead>
          <tbody>
            {(rfq.parts || []).map(p => (
              <tr key={p.part_id} className="border-t border-slate-100">
                <td className="px-4 py-2">{p.part_name}</td>
                <td className="px-4 py-2 font-mono">{p.part_number}</td>
                <td className="px-4 py-2">{Number(p.monthly_volume_pcs || 0).toLocaleString("en-IN")} <span className="text-slate-400">≈ {Number((p.monthly_volume_pcs || 0) * 12).toLocaleString("en-IN")}/yr</span></td>
                <td className="px-4 py-2">{p.target_price_per_pc ? formatINR(p.target_price_per_pc) : "—"}</td>
                <td className="px-4 py-2">{p.drawing_original_filename ? <span className="inline-flex items-center gap-1 text-sky-600"><Paperclip size={12} />{p.drawing_original_filename}</span> : <span className="text-slate-400">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      {(rfq.status_history || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Status history</div>
          <ul className="text-xs text-slate-700 space-y-1">
            {rfq.status_history.map((h, i) => (
              <li key={i}>· {STATUS_LABEL[h.status] || h.status} on {h.at?.slice(0, 10)} by {h.by_name || h.by}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
